<template>
  <div class="background">

    <div class="support" v-if="!formSentSuccess">
      <head-title class="head-title">Support</head-title>
      <div class="support__text">
        Please use the Support Form to&nbsp;receive professional
        help from our specialists. Filling out the form is&nbsp;going
        to&nbsp;help our team to&nbsp;resolve your issue quickly and effectively.
      </div>
      <form class="support__form" novalidate @submit.stop.prevent="tryToSendSupportForm">
        <input-el
          inputId="support-name"
          type="text"
          label="Name"
          name="name"
          class="support__name"
          placeholder="Name"
          @input="serverFieldErrors.name = null"
          v-model.trim="$v.supportData.name.$model"
          :error="$v.supportData.name.$error"
          small
          >
        </input-el>
        <input-el
          inputId="support-email"
          type="email"
          label="Email"
          name="email"
          class="support__email"
          placeholder="Email"
          @input="serverFieldErrors.email = null"
          v-model.trim="$v.supportData.email.$model"
          :error="$v.supportData.email.$error"
          small
        >
        </input-el>
        <textarea-el
          textareaId="support-textarea"
          class="support__textarea"
          placeholder="Text"
          label="Message"
          name="message"
          @input="serverFieldErrors.message = null"
          v-model.trim="$v.supportData.message.$model"
          :error="$v.supportData.message.$error"
          large>
        </textarea-el>
        <div class="g-recaptcha-wrapper">
          <vue-recaptcha
            class="g-recaptcha"
            :class="{ error: $v.supportData.recaptcha.$error }"
            @verify="onVerify"
            ref="recaptcha"
            :sitekey="recaptchaKey"
          />
        </div>
        <div class="errors">
          <div class="input-alert" v-if="$v.supportData.recaptcha.$error">
            Please validate captcha!
          </div>
          <div class="input-alert" v-if="error">
            Something went wrong. Try again!
          </div>
        </div>
        <button-el :disabled="requestInProgress" red left large class="support__send-btn">
          Send
          <icon-base :red="!requestInProgress" :gray="requestInProgress">
            <icon-check />
          </icon-base>
        </button-el>
      </form>
    </div>

    <div v-else>
      <div class="message-sent">
        <router-link to="/">
          <button-el class="message-sent__close" >
            <icon-base red>
              <icon-cross />
            </icon-base>
          </button-el>
        </router-link>
        <head-title class="head-title">Message is sent</head-title>
        <div class="message-sent__text">
          We will answer you on as soon as possible!
        </div>

        <router-link to="/">
          <button-el red left large class="message-sent__btn">
            Close
            <icon-base red>
              <icon-check />
            </icon-base>
          </button-el>
        </router-link>
      </div>
    </div>

  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { required, email } from 'vuelidate/lib/validators';
import validateFormMixin from 'src/mixins/validateForm';
import { recaptchaKey } from 'src/utils/config';
import { IconCheck, IconCross } from 'Icons';

export default {
  mixins: [
    validateFormMixin,
  ],
  data: () => ({
    serverFieldErrors: {
      name: null,
      email: null,
      message: null,
      recaptcha: null,
    },
    error: null,
    formSentSuccess: null,
    support: null,
    recaptchaKey,
    supportData: {
      name: '',
      email: '',
      message: '',
      recaptcha: null,
    },
    requestInProgress: false,
  }),
  validations: {
    supportData: {
      name: {
        required,
        serverValidity() {
          return !this.serverFieldErrors.name;
        },
      },
      email: {
        required,
        email,
        serverValidity() {
          return !this.serverFieldErrors.email;
        },
      },
      message: {
        required,
        serverValidity() {
          return !this.serverFieldErrors.message;
        },
      },
      recaptcha: {
        required,
        serverValidity() {
          return !this.serverFieldErrors.recaptcha;
        },
      },
    },
  },
  components: {
    IconCheck,
    IconCross,
    VueRecaptcha,
  },
  methods: {
    onVerify(response) {
      this.supportData.recaptcha = response;
      this.serverFieldErrors.recaptcha = null;
    },
    tryToSendSupportForm() {
      this.validateForm(null, this.sendForm);
    },
    resetRecaptcha() {
      this.supportData.recaptcha = null;
      this.$refs.recaptcha.reset();
    },
    sendForm() {
      this.requestInProgress = true;
      this.$apiService.sendMessageToSupport(this.supportData).then(() => {
        this.formSentSuccess = true;
        this.clearFields();
      }).catch(({ error, fieldErrors }) => {
        this.error = error;
        this.resetRecaptcha();
        if (fieldErrors) {
          this.serverFieldErrors = fieldErrors;
        }
      }).finally(() => {
        this.requestInProgress = false;
      });
    },
    clearFields() {
      this.supportData.name = '';
      this.supportData.email = '';
      this.supportData.message = '';
      this.supportData.recaptcha = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.background {
  @include background;
}
$w: 570px;
.support {
  max-width: $w;
  width: 100%;
  background: $white-color2;
  border-radius: 5px;
  margin: 0 auto;
  padding-bottom: 30px;
  &__text {
    @include font(13px, $black-color1);
    line-height: 21px;
    padding: 34px 95px 43px 31px;
  }
  &__form {
    padding: 0 30px;
  }
  &__send-btn {
    margin-top: 38px;
    padding: 10.5px 12px 10.5px 18px;
  }
  &__name {
    max-width: 180px;
    display: inline-block;
    margin-right: 27px;
  }
  &__email {
    max-width: 299px;
    display: inline-block;
    width: 100%;
  }
  &__textarea {
    margin-top: 36px;
  }
  .head-title {
    padding: 30px 30px 33px;
    border-bottom: 1px solid $grey-color3;
  }
}
.message-sent {
  max-width: 365px;
  width: 100%;
  background: $white-color2;
  position: relative;
  border-radius: 5px;
  margin: auto;
  .head-title {
    padding: 31px 23px 31px;
    border-bottom: 1px solid $grey-color3;
  }
  .message-sent__close {
    @include removeBtnDefaults;
    position: absolute;
    right: 30px;
    max-width: 12px;
    top: 30px;
  }
  &__text {
    padding: 29px 30px 41px;
  }
  &__btn {
    margin: 0 30px 30px;
    padding: 11px 10px 10px 18px;
  }
}
.g-recaptcha-wrapper {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}
.input-alert {
  color: $red-color3;
  margin-top: 10px;
}
.errors {
  text-align: center;
}

$side-padding: 10px;
$bp: $w + ($side-padding * 2);
@media screen and (max-width: $bp) {
  .support {
    width: calc(100% - (#{$side-padding} * 2));
    &__email {
      margin-top: 29px;
      display: block;
    }
    &__textarea {
      margin-top: 29px;
    }
    &__text {
      padding: 38px 19px 38px;
      text-align: center;
    }
    &__form {
      padding: 0 10px;
    }
    &__send-btn {
      margin-left: auto;
      margin-right: auto;
      display: block;
    }
  }
  .background {
    padding: 10px 0;
  }
  .g-recaptcha {
    transform: scale(0.8);
    display: inline-block;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/pages/Support.vue